import { useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import css from '@styled-system/css';
import { BLOCKS } from '@contentful/rich-text-types';

import { contentfulImageType, contentfulRichTextType } from 'types';
import getContentfulImgixUrl from 'utils/getContentfulImgixUrl';
import Section from 'legacy/components/section';
import Flex from 'legacy/components/flex';
import Box from 'legacy/components/box';
import Heading from 'legacy/components/heading';
import Text from 'legacy/components/text';
import Image from 'legacy/components/image';
import Cta from 'legacy/components/cta';
import SimpleRichText from 'legacy/components/humanity-v1/primitives/simpleRichText';

const StyledImageMobile = styled(Image)(({ sectionSize }) =>
  css({
    display: ['block', null, null, 'none'],
    position: 'relative',
    bottom: sectionSize === 'large' ? '-6rem' : '-4.5rem',
    mt: [-4, null, null, 0],
    width: '100%',
    maxHeight: '30rem',
    overflow: 'hidden',
    '& img': {
      display: 'block',
      position: 'relative',
      top: 'auto',
      right: '20vw',
      minWidth: '140vw',
      height: 'auto',
      borderTopLeftRadius: '50%',
      borderTopRightRadius: '50%',
      overflow: 'hidden',
    },
  })
);

const StyledImageDesktop = styled(Image)(({ contentPosition }) =>
  css({
    display: ['none', null, null, 'block'],
    position: 'absolute',
    top: 0,
    right: contentPosition !== 'right' && '-50vw',
    left: contentPosition === 'right' && '-20vw',
    mt: 0,
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    '& img': {
      position: 'relative',
      top: ['-10%', null, null, '-25%'],
      right: 0,
      height: '150%',
      borderTopLeftRadius: contentPosition !== 'right' && '200%',
      borderBottomLeftRadius: contentPosition !== 'right' && '200%',
      borderTopRightRadius: contentPosition === 'right' && '200%',
      borderBottomRightRadius: contentPosition === 'right' && '200%',
      overflow: 'hidden',
    },
  })
);

const YellowLarge = styled('span')(
  css({
    fontSize: [6, null, null, 8],
    lineHeight: 0,
    color: 'yellow',
  })
);

const Content = styled(Box)(
  css({
    '& > p': {
      color: 'white',
      '& > a': {
        fontWeight: 'bold',
        color: 'teal',
      },
    },
  })
);

const Highlight = styled.span(
  css({
    color: 'teal',
  })
);

const StyledSection = styled(Section)(
  css({
    position: 'relative',
    overflow: 'hidden',
  })
);

const bodyRenderOpts = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant={['default', null, null, 'large']} color="white">
        {children}
      </Text>
    ),
  },
};

const SecondaryMasthead = ({
  size = 'medium',
  backgroundColor = 'blue',
  eyebrow = '',
  heading,
  headingHighlight = '',
  mobileImage = null,
  desktopImage = null,
  image = null,
  cta = null,
  body = null,
  contentPosition = 'left',
  children = '',
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const highlightIndex = heading.indexOf(headingHighlight);

  return (
    <StyledSection variant={size} bg={backgroundColor} {...props}>
      <Section.Inner>
        <Flex
          justifyContent={contentPosition === 'right' ? 'flex-end' : 'flex-start'}
          flexDirection={['column', null, null, 'row']}
          position="relative"
        >
          <Box width={[1, null, null, '28rem', '36rem']}>
            {!!eyebrow?.length && (
              <Text
                variant="eyebrowMasthead"
                color={backgroundColor === 'blue' ? 'white' : 'blue'}
              >
                {eyebrow}
              </Text>
            )}
            <Heading
              as="h1"
              variant={['h4', null, 'h3', null, 'h2']}
              mt={0}
              color={backgroundColor === 'blue' ? 'white' : 'blue'}
            >
              {!headingHighlight?.length && heading}
              {!!headingHighlight?.length && (
                <>
                  {heading.substring(0, highlightIndex)}
                  <Highlight>{headingHighlight}</Highlight>
                  {heading.substring(highlightIndex + headingHighlight.length)}
                </>
              )}
              <YellowLarge>.</YellowLarge>
            </Heading>
            <Content>
              {body && (
                <SimpleRichText content={body.json} renderOptions={bodyRenderOpts} />
              )}
              {children}
            </Content>
            {cta?.type === 'button' && (
              <Cta
                type="button"
                label={cta.label}
                url={cta.url}
                location="header"
                btnSize="lg"
                btnVariant="secondary"
                display={['block', null, 'inline-block']}
                mt={4}
              />
            )}
            {cta?.type === 'link' && (
              <Cta
                type="link"
                label={cta.label}
                url={cta.url}
                location="header"
                display="block"
                mt={[2, null, null, 4]}
                color="teal"
              />
            )}
          </Box>
        </Flex>
      </Section.Inner>
      <StyledImageDesktop
        src={getContentfulImgixUrl(desktopImage ? desktopImage.url : image.url)}
        htmlAttributes={{
          alt: desktopImage ? desktopImage.altText : image.altText,
        }}
        sizes={`(min-width: ${theme.breakpoints.lg}) 80vw, 90vw`}
        additionalParams={{ ar: '1:1', fit: 'crop' }}
        contentPosition={contentPosition}
      />
      <StyledImageMobile
        src={getContentfulImgixUrl(mobileImage ? mobileImage.url : image.url)}
        htmlAttributes={{
          alt: mobileImage ? mobileImage.altText : image.altText,
        }}
        sizes={`(min-width: ${theme.breakpoints.lg}) 80vw, 90vw`}
        sectionSize={size}
      />
    </StyledSection>
  );
};

SecondaryMasthead.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  backgroundColor: PropTypes.oneOf(['blue', 'white']),
  eyebrow: PropTypes.string,
  heading: PropTypes.string.isRequired,
  headingHighlight: PropTypes.string,
  image: PropTypes.shape({
    altText: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  mobileImage: contentfulImageType,
  desktopImage: contentfulImageType,
  cta: PropTypes.shape({
    type: PropTypes.oneOf(['button', 'link']).isRequired,
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  body: contentfulRichTextType,
  children: PropTypes.node,
  contentPosition: PropTypes.oneOf(['left', 'right']),
};

export default SecondaryMasthead;
